import { Component, ElementRef, Input, OnChanges, ViewChild } from '@angular/core';
import { QrRenderService } from '../core/services/qr-render.service';

/**
 * The invite as a printed object.
 *
 * This card used to try to be two things — a confirmation on screen and an
 * image to send into a chat. It is no longer the second: sharing now sends the
 * link, and generate-preview-url answers i.picaggo.com/{hex} with Open Graph
 * tags so the chat preview builds itself from the event's own cover photo.
 *
 * What is left is the job a link cannot do — the thing a host prints and
 * stands on a table. So it is laid out at A-series proportion with the code
 * taking most of the width, to be read from across a room rather than held in
 * a hand.
 *
 * Presentational on purpose: it owns no links, no analytics and no share
 * plumbing, because the create sheet (one collaborator link) and the invite
 * dialog (switching between collaborator and viewer) resolve those very
 * differently.
 */
@Component({
  selector: 'app-invite-card',
  templateUrl: './invite-card.component.html',
  styleUrls: ['./invite-card.component.scss'],
})
export class InviteCardComponent implements OnChanges {
  @Input() eventName = '';
  /** e.g. "Until Fri 28 Aug, 12:45 PM". Hidden when empty. */
  @Input() whenLabel = '';

  /**
   * The URL the code should encode. Must match what the server would encode —
   * the collaborator hash or the viewer hash — or the scan counts split.
   */
  @Input() qrUrl = '';

  /** The server-rendered code, used if drawing one here fails. */
  @Input() qrImage = '';

  @Input() loading = false;

  /**
   * Whether this code lets guests add photos or only look at them.
   *
   * The invite dialog issues either, and the difference has to show on the
   * card — "Scan to add your photos" over a view-only code is a promise the
   * link does not keep.
   */
  @Input() canUpload = true;

  /** Drawn locally from qrUrl; falls back to qrImage. */
  renderedQr = '';

  /** The element the export service rasterises. */
  @ViewChild('card', { static: true }) private cardRef!: ElementRef<HTMLElement>;

  constructor(private qrRender: QrRenderService) { }

  /** Resolves once the current code has been drawn. */
  private renderTask: Promise<void> = Promise.resolve();

  ngOnChanges(): void {
    if (!this.qrUrl) return;
    this.renderTask = this.qrRender
      .render(this.qrUrl)
      .then((url) => { if (url) this.renderedQr = url; })
      .catch(() => { /* the server code stands in */ });
  }

  /**
   * Await before rasterising. The code is drawn asynchronously, so an export
   * fired on the same tick would capture the card with the previous code on
   * it — or with none at all.
   */
  whenReady(): Promise<void> {
    return this.renderTask;
  }

  get element(): HTMLElement {
    return this.cardRef.nativeElement;
  }

  get qr(): string {
    return this.renderedQr || this.qrImage;
  }

  get eyebrow(): string {
    return this.canUpload ? "You're invited" : "You're invited to view";
  }

  get scanLine(): string {
    return this.canUpload ? 'Scan to add your photos' : 'Scan to see the photos';
  }
}
